import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import AddModal, { ItemCodeData } from "@/components/modal/standard/itemCode/Add";
import UpdateModal from "@/components/modal/standard/itemCode/Update";
import { useItemCode, useDeleteItemCode, useToggleFavorite } from "@/hooks/standard/st_item_code/useItemCode";
import { ItemCode } from "@/interface/standard/st_item_code";
import { format } from "date-fns";
import { toast } from "sonner";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import StarIcon from "@/components/ui/StarIcon";
import { getToday } from "@/lib/date";

export default function ItemCodePage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [exactMatch, setExactMatch] = useState(false); // 정확히 일치 체크박스
  const [actualExactMatch, setActualExactMatch] = useState(false); // 실제 검색에 사용할 정확히 일치
  const [startDate, setStartDate] = useState("");
  const [startDateBtnValue, setStartDateBtnValue] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endDateBtnValue, setEndDateBtnValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<ItemCode[]>([]);
  const [totalElements, setTotalElements] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedItemCode, setSelectedItemCode] = useState<ItemCode | undefined>(undefined);
  const [tableVer, setTableVer] = useState(0);

  const deleteMutation = useDeleteItemCode();
  const toggleFavoriteMutation = useToggleFavorite();

  const {
    data: itemCodeData,
    isLoading,
    error,
  } = useItemCode({
    page: currentPage,
    size: 20,
    search: actualSearchValue,
    startDate: startDateBtnValue,
    endDate: endDateBtnValue,
    sort: "stItemCode,desc",
    exactMatch: actualExactMatch,
  });

  // 즐겨찾기 토글 핸들러
  const handleToggleFavorite = async (stItemCode: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 행 선택 방지
    try {
      await toggleFavoriteMutation.mutateAsync(stItemCode);
      // 데이터 새로고침
      setAllData([]);
      setTotalElements(0);
      setCurrentPage(0);
      setTableVer(v => v + 1);
    } catch (error) {
      console.error("즐겨찾기 토글 실패:", error);
    }
  };

  // 테이블 컬럼 정의 (그룹 헤더 예시)
  const columns: TableColumn[] = [
    {
      key: "isFavorite",
      header: "즐겨찾기",
      // width: "40px",
      render: (value: boolean, row: ItemCode) => (
        <StarIcon filled={value} onClick={e => handleToggleFavorite(row.stItemCode, e)} />
      ),
    },
    { key: "stItemCode", header: "품목코드" },
    { key: "createdAt", header: "등록일자", render: (value: string) => format(new Date(value), "yyyy-MM-dd") },
    { key: "stItemGroupName", header: "품목구분" },
    { key: "stItemName", header: "품목명", bodyAlign: "left" },
    { key: "stItemSize", header: "규격" },
    { key: "stItemUnit", header: "단위" },
    { key: "stItemCasNo", header: "Cas 번호" },
    { key: "sfsgwd", header: "유해위험분류" },
    {
      key: "stItemUse",
      header: "사용여부",
      render: (value: string | number) => (String(value) === "1" || value === 1 ? "사용" : "미사용"),
    },
    { key: "stItemRemark", header: "비고", bodyAlign: "left" },
  ];

  useEffect(() => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setLoadingMore(false);
  }, [actualSearchValue, actualExactMatch]);

  // 데이터 누적 로직
  useEffect(() => {
    if (itemCodeData && itemCodeData.content && itemCodeData.content.length > 0) {
      if (currentPage === 0) {
        setAllData(itemCodeData.content);
        setTotalElements(itemCodeData.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...itemCodeData.content]);
      }
    } else if (itemCodeData && (!itemCodeData.content || itemCodeData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [itemCodeData, currentPage]);

  const hasMore = useMemo(() => {
    if (!itemCodeData) return false;
    return !itemCodeData.last && allData.length < totalElements;
  }, [itemCodeData, allData.length, totalElements]);

  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  useEffect(() => {
    if (itemCodeData) {
      setLoadingMore(false);
    }
  }, [itemCodeData]);

  useEffect(() => {
    if (selectedRows.length === 1) {
      const selectedData = allData[selectedRows[0]];
      setSelectedItemCode(selectedData);
    } else {
      setSelectedItemCode(undefined);
    }
  }, [selectedRows, allData]);

  // 무한 스크롤 페이지 hook 사용
  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore) {
        setCurrentPage(prev => prev + 1);
        setTotalElements(prev => prev + 20);
      }
    },
  });

  const handleSearch = useCallback(() => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    // 날짜가 비어있으면 현재 날짜로 설정
    const finalStartDate = startDate || getToday();
    const finalEndDate = endDate || getToday();

    // 조회 버튼을 누를 때만 실제 검색값 설정
    setActualSearchValue(searchValue);
    setActualExactMatch(exactMatch);
    setStartDateBtnValue(finalStartDate);
    setEndDateBtnValue(finalEndDate);
  }, [searchValue, exactMatch, startDate, endDate]);

  // 리셋 핸들러 수정
  const handleReset = () => {
    if (searchValue === "" && actualSearchValue === "" && currentPage === 0) {
      return;
    }
    setSearchValue("");
    setExactMatch(false);
    setSelectedRows([]);
    if (actualSearchValue !== "" || actualExactMatch) {
      setActualSearchValue("");
      setActualExactMatch(false);
      setCurrentPage(0);
      setTotalElements(0);
      setLoadingMore(false);
      setAllData([]);
    }

    if (currentPage > 0) {
      setCurrentPage(0);
      setTableVer(v => v + 1);
    }
  };

  function handleSave(data: ItemCodeData): void {
    // 데이터 새로고침
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectedItemCode(undefined);
    setTableVer(v => v + 1);
  }

  function handleUpdate(): void {
    // 데이터 새로고침
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectedItemCode(undefined);
    setStartDateBtnValue("");
    setEndDateBtnValue("");
    setTableVer(v => v + 1);
  }

  const handleUpdateClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("수정할 품목을 선택해주세요.");
      return;
    }
    if (selectedRows.length > 1) {
      toast.warning("하나의 품목만 선택해주세요.");
      return;
    }
    setIsUpdateModalOpen(true);
    setTableVer(v => v + 1);
  };

  const handleDeleteClick = async () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 품목을 선택해주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedRows.length}개의 품목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      for (const index of selectedRows) {
        const item = allData[index];
        if (item?.stItemCode) {
          await deleteMutation.mutateAsync(item.stItemCode);
        }
      }

      // 데이터 새로고침
      setAllData([]);
      setTotalElements(0);
      setCurrentPage(0);
      setSelectedRows([]);
      setSelectedItemCode(undefined);
      setTableVer(v => v + 1);
    } catch (error) {
      console.error("삭제 실패:", error);
    }
  };

  // 로딩 중일 때 표시할 내용
  if (isLoading && allData.length === 0) {
    return <div className="flex items-center justify-center h-full">로딩 중...</div>;
  }

  // 에러가 발생했을 때 표시할 내용
  if (error && allData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        데이터를 불러오는 중 오류가 발생했습니다.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <InputBox
            label="품목명"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          {/* <DateBox
            label="등록일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          /> */}

          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PermissionButton
              permission="canCreate"
              variant="etc"
              onClick={() => {
                setSelectedRows([]);
                setIsModalOpen(true);
              }}
            >
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdateClick}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
              삭제
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="품목코드" />
            <ExcelButton data={allData} columns={columns} fileName="품목코드" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`item-code-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
            maxHeight="100%"
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
        {allData.length === 0 && (
          <div className="text-center py-2 text-gray-500">
            데이터가 없습니다. (isLoading: {isLoading.toString()}, allData.length: {allData.length})
          </div>
        )}
      </main>

      <AddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} />
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdate={handleUpdate}
        itemData={selectedItemCode}
      />
    </div>
  );
}
